import React from 'react';
import { unescapeHTML } from 'underscore.string';
import moment from 'moment';
import AccessorialRequestStatusBadge from 'components/v2/AccessorialRequestStatusBadge';
import { t } from 'i18next';
import formatMoney from 'utils/formatters/Money';
import { sluggify } from 'utils/localization/utils';
import { CONFIGURED_FEATURES, featureEnabled } from 'helpers/feature-flags';

const WEIGHT_AFFECTED_ACCESSORIALS = ['overweight'];
const ORDER_CREATION_DATE = 'ORDER_CREATION_DATE';
const PICKUP_STOP = 'PICKUP_STOP';
const DELIVERY_EMPTY = 'DELIVERY_EMPTY';

const getElapsedTime = (time) => {
  const now = moment();
  const timeToDiff = moment(time);
  const days = now.diff(timeToDiff, 'days');
  const hours = now.diff(timeToDiff, 'hours');
  const minutes = now.diff(timeToDiff, 'minutes');
  const seconds = now.diff(timeToDiff, 'seconds');

  if (days > 0) {
    return `${days}d`;
  }

  if (hours > 0) {
    return `${hours}h`;
  }

  if (minutes > 0) {
    return `${minutes}m`;
  }
  return `${seconds}s`;
};

function getStop(stops, type, empty = false) {
  if (type !== 'origin' && type !== 'destination') {
    return null;
  }
  const action = type === 'origin' ? 'pickup' : 'deliver';
  const matchingStop = stops.find(
    (stop) => stop.action === action && stop.empty === empty,
  );
  return matchingStop || stops[0];
}

const updatedFormatter = (_cell, row) => {
  return (
    <p style={{ color: '#00a9db' }}>
      {paneLinkFormatter(
        getElapsedTime(row.modified_at || row.created_at),
        row,
      )}
    </p>
  );
};

const paneLinkFormatter = (cell, row) => {
  const accessorialType = row?.accessorial_type;
  const weight = row?.shipment?.weight || 0;
  return (
    <div className="flex-col">
      <a onClick={() => row.showAccessorialRequestPane(row)}>{cell}</a>
      {featureEnabled(CONFIGURED_FEATURES.SSR) &&
        WEIGHT_AFFECTED_ACCESSORIALS.includes(accessorialType) && (
          <p className="py-2">
            {t('TOTAL_WEIGHT')} {weight}
            {DEFAULT_WEIGHT_UNITS}
          </p>
        )}
    </div>
  );
};

const statusFormatter = (_cell, row) => {
  const { shipper_status, carrier_status } = row;
  return (
    <div>
      <AccessorialRequestStatusBadge status={shipper_status} />
      <div className="mt-4">
        <AccessorialRequestStatusBadge status={carrier_status} />
      </div>
    </div>
  );
};

const deliveryFormatter = (_cell, row) => {
  if (row.shipment.deliver_after && row.shipment.deliver_by) {
    return (
      <ul>
        <li>
          {moment(row.shipment.deliver_after).format('MMMM Do YYYY, h:mm:ss a')}
        </li>
        <li>
          {moment(row.shipment.deliver_by).format('MMMM Do YYYY, h:mm:ss a')}
        </li>
      </ul>
    );
  }
};

const requestedFormatter = (_cell, row) => {
  if (row.created_at) {
    return (
      <ul>
        <li>{moment(row.created_at).format('MMMM Do YYYY, h:mm:ss a')}</li>
        <p>({t('TIMEZONE_NAME')})</p>
      </ul>
    );
  }
};

const approvalDueDateFormatter = (_cell, row) => {
  const { approval_due_date } = row;
  if (approval_due_date) {
    return (
      <ul>
        <li>{moment(approval_due_date).format('MMMM Do YYYY, h:mm:ss a')}</li>
        <p>({t('TIMEZONE_NAME')})</p>
      </ul>
    );
  }
};

const rateSourceFormatter = (_cell) => {
  return <div>{t(sluggify(_cell)) || ''}</div>;
};

const originFormatter = (_, row) => {
  return <div>{row.shipment?.origin[0]?.address || ''}</div>;
};

const destinationFormatter = (_, row) => {
  return <div>{row.shipment?.destination[0]?.address || ''}</div>;
};

const carrierCostFormatter = (_cell, row) => {
  return <div>{formatMoney(row.carrier_cost || 0, row?.currency)}</div>;
};

const shipperCostFormatter = (_cell, row) => {
  return <div>{formatMoney(row.shipper_cost || 0, row?.currency)}</div>;
};

const shipmentFormatter = (cell, row) => {
  return <a onClick={() => row.showShipmentModal(row.shipment._id)}>{cell}</a>;
};

const payeeTypeFormatter = (_cell, row) => {
  if (row.payee_type === 'carrier') {
    return <div>{row.carrier_name || ''}</div>;
  }
  if (row.payee_type === 'vendor') {
    return <div>{row.vendor_name || ''}</div>;
  }
  if (row.payee_type === 'partner') {
    return <div>{row.partner_name || ''}</div>;
  }
};

const nameFormatter = (cell) => <span>{unescapeHTML(cell)}</span>;

const columns = [
  {
    dataField: 'shipment_id',
    text: 'Shipment',
    sort: true,
    formatter: shipmentFormatter,
    localizationSlug: 'SHIPMENT',
    count: 1,
    titleize: true,
  },
  {
    dataField: 'equipment_id',
    text: 'Equipment',
    sort: true,
    formatter: nameFormatter,
    localizationSlug: 'EQUIPMENT',
    titleize: true,
  },
  {
    dataField: 'shipper_name',
    text: 'Shipper',
    formatter: nameFormatter,
    sort: true,
    localizationSlug: 'SHIPPER',
    count: 1,
    titleize: true,
  },
  {
    dataField: 'carrier_name',
    text: 'Carrier',
    formatter: nameFormatter,
    sort: true,
    localizationSlug: 'CARRIER',
    count: 1,
    titleize: true,
  },
  {
    dataField: 'message',
    text: 'Accessorial',
    sort: true,
    formatter: paneLinkFormatter,
    localizationSlug: 'ACCESSORIAL',
    count: 1,
    titleize: true,
  },
  {
    dataField: 'shipper_cost',
    text: 'Shipper Cost',
    formatter: shipperCostFormatter,
    sort: true,
    localizationSlug: 'SHIPPER_COST',
    titleize: true,
  },
  {
    dataField: 'receivable_rate_source',
    text: 'Shipper Source',
    formatter: rateSourceFormatter,
    sort: false,
    localizationSlug: 'SHIPPER_SOURCE',
    titleize: true,
  },
  {
    dataField: 'carrier_cost',
    text: 'Carrier Cost',
    formatter: carrierCostFormatter,
    sort: true,
    localizationSlug: 'CARRIER_COST',
    titleize: true,
  },
  {
    dataField: 'payable_rate_source',
    text: 'Carrier Source',
    formatter: rateSourceFormatter,
    sort: false,
    localizationSlug: 'CARRIER_SOURCE',
    titleize: true,
  },
  {
    dataField: 'status',
    text: 'Status',
    sort: true,
    formatter: statusFormatter,
    localizationSlug: 'STATUS',
    titleize: true,
  },
  {
    dataField: 'created_by',
    text: 'Created by',
    sort: true,
    localizationSlug: 'CREATED_BY',
    titleize: true,
  },
  {
    dataField: 'carrier_decline_reason_code',
    text: 'Reason Code',
    sort: false,
    localizationSlug: 'REASON_CODE',
    titleize: true,
  },
  {
    dataField: 'payee_type',
    text: 'Payee',
    sort: true,
    formatter: payeeTypeFormatter,
    localizationSlug: 'PAYEE',
    titleize: true,
  },
  {
    dataField: 'origin',
    text: 'Origin Address',
    sort: true,
    formatter: originFormatter,
    localizationSlug: 'ORIGIN_ADDRESS',
    titleize: true,
  },
  {
    dataField: 'destination',
    text: 'Destination Address',
    sort: true,
    formatter: destinationFormatter,
    localizationSlug: 'DESTINATION_ADDRESS',
    titleize: true,
  },
  {
    dataField: 'action_required',
    text: 'Action Required',
    sort: true,
    formatter: paneLinkFormatter,
    localizationSlug: 'ACTION_REQUIRED',
    titleize: true,
  },
  {
    dataField: 'approval_due_date',
    text: 'Approval Due Date',
    sort: true,
    formatter: approvalDueDateFormatter,
    localizationSlug: 'APPROVAL_DUE_DATE',
    titleize: true,
  },
  {
    dataField: 'delivery_by',
    text: 'Delivery',
    sort: false,
    formatter: deliveryFormatter,
    localizationSlug: 'DELIVERY',
    titleize: true,
  },
  {
    dataField: 'created_at',
    text: 'Requested',
    sort: false,
    formatter: requestedFormatter,
    localizationSlug: 'REQUESTED',
    titleize: true,
  },
  {
    dataField: 'modified_at',
    text: 'Updated',
    sort: true,
    formatter: updatedFormatter,
    localizationSlug: 'UPDATED',
    titleize: true,
  },
];

export default {
  columns,
};
